import { useShouldShowInContextAnalysis } from '@modules/analysis/hooks';
import { DashboardAnalysisSubtitle } from '@modules/dashboard/components/DashboardAnalysisHeader/DashboardAnalysisSubtitle';
import {
  getAnalysisTypeDisplayName,
  trackDashboardEvent,
} from '@modules/dashboard/helpers';
import {
  useGetDashboardAnalysisLinkWithState,
  useGetDashboardLinkWithGlobalFilterParams,
} from '@modules/dashboard/hooks';
import { useDashboardStore } from '@modules/dashboard/store/useDashboardStore';
import { FeedDrawerContainer } from '@modules/feed/components/FeedDrawerContainer';
import { QuantifyDrawerContainer } from '@modules/quantify/components/QuantifyDrawerContainer';
import { Box, Stack, Tooltip, alpha } from '@mui/material';
import {
  AnalysisUseCase,
  AnalysisViewFragment,
  Status,
} from 'generated/graphql';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardAnalysisHeaderActions } from './DashboardAnalysisHeaderActions';
import { DashboardAnalysisTitle } from './DashboardAnalysisTitle';

interface DashboardAnalysisHeaderProps {
  rowID: string;
  analysis: AnalysisViewFragment;
  shouldDisableInteraction?: boolean;
}

export const DashboardAnalysisHeader = ({
  rowID,
  analysis,
  shouldDisableInteraction: shouldDisableInteractionProp = false,
}: DashboardAnalysisHeaderProps) => {
  const navigate = useNavigate();
  const [shouldOpenQuantifyDrawer, setShouldOpenQuantifyDrawer] =
    useState(false);
  const [shouldOpenFeedDrawer, setShouldOpenFeedDrawer] = useState(false);
  const shouldShowInContextAnalysis = useShouldShowInContextAnalysis();
  const globalFilterDuration = useDashboardStore(({ query }) => query.duration);
  const globalFilterWhereClause = useDashboardStore(
    ({ query }) => query.whereClause,
  );

  const { analysisLink, analysisLinkState } =
    useGetDashboardAnalysisLinkWithState(analysis);

  const isDefaultContext = useDashboardStore(
    ({ visual }) => visual.context === 'Default',
  );

  const dashboardLinkWithGlobalFilters =
    useGetDashboardLinkWithGlobalFilterParams();

  const openAnalysis = () => {
    if (shouldShowInContextAnalysis) {
      if (analysis.useCase === AnalysisUseCase.Feed) {
        setShouldOpenFeedDrawer(true);
      } else {
        setShouldOpenQuantifyDrawer(true);
      }
    } else {
      if (isDefaultContext) {
        window.history.replaceState(null, '', dashboardLinkWithGlobalFilters);
      }

      navigate(analysisLink, { state: analysisLinkState });
    }

    trackDashboardEvent({
      eventName: `Analysis Opened from Dashboard`,
      dashboardLink: window.location.href,
      analysisLink,
    });
  };

  const isAnalysisArchived = analysis.status === Status.Archived;

  const shouldDisableInteraction =
    shouldDisableInteractionProp || isAnalysisArchived;

  const [showHeaderTooltip, setShowHeaderTooltip] = useState<boolean>(false);

  const handleTooltip = (show: boolean) => {
    setShowHeaderTooltip(show);
  };

  const getTooltipTitle = () => {
    return (
      <Stack spacing={0.5}>
        <Box>
          {getAnalysisTypeDisplayName(analysis.useCase)}: {analysis.title}
        </Box>
        <Box>
          {shouldShowInContextAnalysis
            ? `(Click to expand)`
            : `(Click to open in ${getAnalysisTypeDisplayName(
                analysis.useCase,
              )} page)`}
        </Box>
      </Stack>
    );
  };

  return (
    <Box>
      <Tooltip
        open={showHeaderTooltip}
        placement="top"
        title={getTooltipTitle()}
        arrow
        onMouseEnter={() => setShowHeaderTooltip(true)}
        onMouseLeave={() => setShowHeaderTooltip(false)}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          pl={2}
          py={1}
          spacing={1}
          sx={{
            borderBottom: '1px solid',
            borderColor: 'divider',
            '&:hover': {
              backgroundColor: (theme) =>
                alpha(theme.palette.text.primary, 0.08),
            },
          }}
        >
          <Stack
            direction="column"
            sx={{
              cursor: shouldDisableInteraction ? undefined : 'pointer',
              width: '100%',
            }}
            onClick={shouldDisableInteraction ? undefined : openAnalysis}
            className="dashboard-analysis-header"
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box width="calc(100% - 70px)">
                <DashboardAnalysisTitle analysis={analysis} />
              </Box>
              {!shouldDisableInteraction && (
                <Box
                  onClick={(event) => event.stopPropagation()}
                  onMouseEnter={() => handleTooltip(false)}
                  onMouseLeave={() => handleTooltip(true)}
                >
                  <DashboardAnalysisHeaderActions
                    rowID={rowID}
                    analysis={analysis}
                    analysisLink={analysisLink}
                  />
                </Box>
              )}
            </Stack>
            <DashboardAnalysisSubtitle analysis={analysis} />
          </Stack>
        </Stack>
      </Tooltip>
      <QuantifyDrawerContainer
        analysis={analysis}
        isOpen={shouldOpenQuantifyDrawer}
        onClose={() => setShouldOpenQuantifyDrawer(false)}
        rowID={rowID}
        globalFilterDuration={globalFilterDuration}
        globalFilterWhereClause={globalFilterWhereClause}
      />
      <FeedDrawerContainer
        analysis={analysis}
        isOpen={shouldOpenFeedDrawer}
        onClose={() => setShouldOpenFeedDrawer(false)}
        rowID={rowID}
        globalFilterDuration={globalFilterDuration}
        globalFilterWhereClause={globalFilterWhereClause}
      />
    </Box>
  );
};
